import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Car, Trash2 } from 'lucide-react';
import { fadeInUp } from '../../lib/animations';
import useLocalStorage from '../../lib/useLocalStorage';

// ─── Tipos & constantes ───────────────────────────────────────────────────────

interface UberState {
  /** Entradas diárias */
  ganhosBrutos: string;
  kmTotal: string;
  precoCombustivel: string;
  gastosExtras: string;
  /** Configurações do veículo (persistidas entre sessões) */
  consumo: string;
  depreciacao: string;
}

const INITIAL: UberState = {
  ganhosBrutos: '',
  kmTotal: '',
  precoCombustivel: '',
  gastosExtras: '',
  consumo: '',
  depreciacao: '0.25',
};

// ─── Utilitários ──────────────────────────────────────────────────────────────

function n(v: string): number {
  return parseFloat(v.replace(',', '.')) || 0;
}

function brl(v: number, decimals = 2): string {
  return v.toLocaleString('pt-BR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ─── Componente ───────────────────────────────────────────────────────────────

interface Props {
  className?: string;
}

const inputCls =
  'w-full rounded-xl bg-slate-800 border border-slate-700 px-4 py-3 text-white placeholder-slate-500 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400';

/**
 * Calculadora de Lucro Líquido para motoristas de app (Uber / 99).
 * Todo o estado é persistido no localStorage (chave "gas2:uber") para que o
 * motorista não precise redigitar o consumo e a depreciação do veículo a cada uso.
 */
const CalculadoraUber: React.FC<Props> = ({ className = '' }) => {
  const [form, setForm, clearForm] = useLocalStorage<UberState>('uber', INITIAL);

  const set =
    (field: keyof UberState) =>
    (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleClear = () => clearForm();

  // ── Cálculos ───────────────────────────────────────────────────────────────
  const ganhos = n(form.ganhosBrutos);
  const km = n(form.kmTotal);
  const preco = n(form.precoCombustivel);
  const consumo = n(form.consumo);
  const extras = n(form.gastosExtras);
  const depreciacao = n(form.depreciacao);

  const litros = consumo > 0 ? km / consumo : 0;
  const custoCombustivel = litros * preco;
  const custoDepreciacao = km * depreciacao;
  const totalCustos = custoCombustivel + custoDepreciacao + extras;
  const lucroLiquido = ganhos - totalCustos;
  const custoKm = km > 0 ? totalCustos / km : 0;
  const lucroKm = km > 0 ? lucroLiquido / km : 0;

  const hasResult = ganhos > 0 && km > 0 && preco > 0 && consumo > 0;

  return (
    <motion.div
      variants={fadeInUp}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className={`rounded-2xl bg-slate-900 border border-slate-800 p-6 sm:p-8 ${className}`}
    >
      {/* Cabeçalho */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
          <Car className="h-5 w-5 text-amber-400" strokeWidth={1.8} />
          Calculadora de Lucro Uber / 99
        </h3>
        <button
          onClick={handleClear}
          title="Limpar dados salvos"
          className="text-slate-500 hover:text-red-400 transition-colors"
        >
          <Trash2 className="h-4 w-4" strokeWidth={1.8} />
        </button>
      </div>

      <div className="space-y-5">
        {/* ── Seção: resultados do dia ── */}
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
          Resultados do dia
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Ganhos Brutos (R$)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Ex: 250,00"
              value={form.ganhosBrutos}
              onChange={set('ganhosBrutos')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              KM Total Rodado
            </label>
            <input
              type="number"
              step="1"
              placeholder="Ex: 180"
              value={form.kmTotal}
              onChange={set('kmTotal')}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Preço Combustível (R$/l)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Ex: 5,89"
              value={form.precoCombustivel}
              onChange={set('precoCombustivel')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Gastos Extras (R$)
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Alimentação, pedágio…"
              value={form.gastosExtras}
              onChange={set('gastosExtras')}
              className={inputCls}
            />
          </div>
        </div>

        {/* ── Seção: configuração do veículo (salva automaticamente) ── */}
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest pt-1">
          Configuração do veículo{' '}
          <span className="normal-case font-normal text-slate-600">
            — salvo automaticamente
          </span>
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Consumo do Carro (km/l)
            </label>
            <input
              type="number"
              step="0.1"
              placeholder="Ex: 12,0"
              value={form.consumo}
              onChange={set('consumo')}
              className={inputCls}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">
              Depreciação/Manutenção{' '}
              <span className="text-slate-500">(R$/km)</span>
            </label>
            <input
              type="number"
              step="0.01"
              placeholder="Padrão: 0,25"
              value={form.depreciacao}
              onChange={set('depreciacao')}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-slate-500">
              Inclui desgaste, óleo, pneus e revisões. Valor sugerido: R$ 0,25/km.
            </p>
          </div>
        </div>

        {/* ── Resultados ── */}
        <AnimatePresence mode="wait">
          {hasResult && (
            <motion.div
              key="result"
              variants={fadeInUp}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0 }}
              className="space-y-3"
            >
              {/* Lucro líquido — destaque */}
              <div
                className={`rounded-2xl p-6 text-center border ${
                  lucroLiquido >= 0
                    ? 'bg-emerald-400/10 border-emerald-400/30'
                    : 'bg-red-400/10 border-red-400/30'
                }`}
              >
                <p className="text-sm font-medium text-slate-400 mb-1">
                  Lucro Líquido do Dia
                </p>
                <p
                  className={`font-display text-4xl font-bold ${
                    lucroLiquido >= 0 ? 'text-emerald-400' : 'text-red-400'
                  }`}
                >
                  R$ {brl(lucroLiquido)}
                </p>
                {lucroLiquido < 0 && (
                  <p className="text-sm text-red-400 mt-2">
                    ⚠️ Você está trabalhando no prejuízo nesse dia.
                  </p>
                )}
              </div>

              {/* Métricas secundárias */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  {
                    label: 'Custo Real/km',
                    value: `R$ ${brl(custoKm, 3)}`,
                    sub: 'todos os custos',
                    color: 'text-orange-400',
                  },
                  {
                    label: 'Lucro/km',
                    value: `R$ ${brl(lucroKm, 3)}`,
                    sub: 'líquido por km',
                    color: lucroKm >= 0 ? 'text-emerald-400' : 'text-red-400',
                  },
                  {
                    label: 'Custo Combustível',
                    value: `R$ ${brl(custoCombustivel)}`,
                    sub: `${brl(litros, 1)} litros`,
                    color: 'text-blue-400',
                  },
                ].map(m => (
                  <div
                    key={m.label}
                    className="rounded-xl bg-slate-800 border border-slate-700 p-4 text-center"
                  >
                    <p className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">
                      {m.label}
                    </p>
                    <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
                    <p className="text-xs text-slate-500 mt-1">{m.sub}</p>
                  </div>
                ))}
              </div>

              {/* Detalhamento de custos */}
              <div className="rounded-xl bg-slate-800 p-4 space-y-2.5">
                <p className="text-xs font-semibold text-slate-300 uppercase tracking-wide">
                  Detalhamento de Custos
                </p>
                {[
                  { label: 'Combustível', value: custoCombustivel },
                  {
                    label: `Depreciação/Manutenção (R$ ${brl(depreciacao, 2)}/km)`,
                    value: custoDepreciacao,
                  },
                  { label: 'Gastos Extras', value: extras },
                ].map(item => (
                  <div key={item.label} className="flex justify-between text-sm">
                    <span className="text-slate-400">{item.label}</span>
                    <span className="text-white font-medium">
                      R$ {brl(item.value)}
                    </span>
                  </div>
                ))}
                <div className="border-t border-slate-700 pt-2 flex justify-between text-sm font-semibold">
                  <span className="text-slate-300">Total de Custos</span>
                  <span className="text-white">R$ {brl(totalCustos)}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!hasResult && (
          <p className="text-xs text-slate-500 text-center">
            Preencha os campos obrigatórios (ganhos, km, combustível e consumo) para calcular.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default CalculadoraUber;

